import { HttpClient, HttpErrorResponse } from "@angular/common/http";
import { environment } from "../../environments/environment";
import { Injectable } from "@angular/core";
import { Observable, throwError, of } from "rxjs";
import { catchError } from "rxjs/operators";
import { TrainData } from "../models/trainDataModel";
import { MessageData } from "../models/messageDataModel";
import { RemarkData } from "../models/remarkDataModel";
import { GetAllWrappedResponse } from "../models/getAllWrappedDataModel";
import { CreateOrUpdateExceptionPayloadModel } from "../models/createOrUpdateExceptionPayloadModel";

@Injectable({
    providedIn: "root"
})
export class API {
    constructor(private httpClient: HttpClient) { }

    private handleError(error: HttpErrorResponse): Observable<never> {
        let errorMessage = 'An unknown error occurred';
        
        if (error.error instanceof ErrorEvent) {
            // Client-side error
            errorMessage = `Client error: ${error.error.message}`;
        } else {
            // Server-side error
            switch (error.status) {
                case 404:
                    errorMessage = `Resource not found (404): ${error.url}`;
                    break;
                case 401:
                    errorMessage = `Unauthorized (401): ${error.url}`;
                    break;
                case 403:
                    errorMessage = `Forbidden (403): ${error.url}`;
                    break;
                case 500:
                    errorMessage = `Internal server error (500): ${error.url}`;
                    break;
                default:
                    errorMessage = `HTTP error ${error.status}: ${error.message}`;
            }
        }
        
        console.error('API Error:', errorMessage, error);
        return throwError(() => new Error(errorMessage));
    }

    private handleTrainDataError(error: HttpErrorResponse): Observable<TrainData> {
        if (error.status === 404) {
            console.log('No active trains available (404) - returning undefined data');
            return of({
                data: undefined,
                delayMeanValue: 0,
                httpCode: 404,
                status: 'No active trains',
                trainOnTimePercentage: 100,
                utc: undefined,
                swedishTime: undefined,
                delayLimit: undefined
            });
        }
        
        return this.handleError(error);
    }

    getTrainData(): Observable<TrainData> {
        return this.httpClient.get<TrainData>(`${environment.LineMapBaseUrl}${environment.trainOnTimeTrainsSuffix}?trainOwner=${environment.trainOwnerSetting}`)
            .pipe(catchError(this.handleTrainDataError.bind(this)));
    }

    getMessageData(): Observable<MessageData[]> {
        return this.httpClient.get<MessageData[]>(environment.MessagesAPiBaseUrl + environment.ActiveMessagesApiUrl)
            .pipe(catchError(this.handleError.bind(this)));
    }

    getRemark(id: RemarkData["id"]): Observable<RemarkData> {
        return this.httpClient.get<RemarkData>(`${environment.LineMapBaseUrl}api/remarks/GetById/${id}?trainOwner=${environment.trainOwnerSetting}`)
            .pipe(catchError(this.handleError.bind(this)));
    }

    updateRemark(remark: RemarkData): Observable<any> {
        return this.httpClient.post(`${environment.LineMapBaseUrl}api/remarks/UpdateRemark`, remark)
            .pipe(catchError(this.handleError.bind(this)));
    }

    createRemark(remark: RemarkData): Observable<RemarkData> {
        return this.httpClient.post<RemarkData>(`${environment.LineMapBaseUrl}api/remarks/CreateRemark`, { ...remark, trainOwner: environment.trainOwnerSetting })
            .pipe(catchError(this.handleError.bind(this)));
    }

    createRemarkException(remarkId: string, exception: CreateOrUpdateExceptionPayloadModel): Observable<CreateOrUpdateExceptionPayloadModel> {
        return this.httpClient.post<CreateOrUpdateExceptionPayloadModel>(`${environment.LineMapBaseUrl}api/remarks/${remarkId}/CreateException`, exception)
            .pipe(catchError(this.handleError.bind(this)));
    }

    updateRemarkException(remarkId: string, exception: CreateOrUpdateExceptionPayloadModel): Observable<CreateOrUpdateExceptionPayloadModel> {
        return this.httpClient.post<CreateOrUpdateExceptionPayloadModel>(`${environment.LineMapBaseUrl}api/remarks/${remarkId}/UpdateException`, exception)
            .pipe(catchError(this.handleError.bind(this)));
    }

    deleteRemarkException(remarkId: string, exceptionId: number): Observable<any> {
        return this.httpClient.get(`${environment.LineMapBaseUrl}api/remarks/${remarkId}/DeleteException/${exceptionId}?trainOwner=${environment.trainOwnerSetting}`)
            .pipe(catchError(this.handleError.bind(this)));
    }

    getRemarks(): Observable<RemarkData[]> {
        return this.httpClient.get<RemarkData[]>(environment.LineMapBaseUrl + environment.remarksGetAllSuffix + environment.trainOwnerSetting)
            .pipe(catchError(this.handleError.bind(this)));
    }

    deleteRemark(id: RemarkData["id"]): Observable<boolean> {
        return this.httpClient.get<boolean>(environment.LineMapBaseUrl + environment.remarkDeleteSuffix + id)
            .pipe(catchError(this.handleError.bind(this)));
    }

    getCurrentAndNextYearTrafficData(): Observable<GetAllWrappedResponse> {
        return this.httpClient.get<GetAllWrappedResponse>(`${environment.LineMapBaseUrl}api/trafficyears/GetCurrentAndNextByCurrentDateWrapped?trainOwner=${environment.trainOwnerSetting}`)
            .pipe(catchError(this.handleError.bind(this)));
    }

    getCurrentYearTrafficData(): Observable<GetAllWrappedResponse> {
        return this.httpClient.get<GetAllWrappedResponse>(`${environment.LineMapBaseUrl}api/trafficyears/GetByCurrentDateWrapped?trainOwner=${environment.trainOwnerSetting}`)
            .pipe(catchError(this.handleError.bind(this)));
    }
}
